import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Compass, Navigation } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { MEDIA, PHONE_RAW, MAPS_DIRECTIONS_URL } from "../lib/data";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-sky-50"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={MEDIA.hero}
          alt="Pálava lake"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sky-900/40 via-sky-900/25 to-sky-900/40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-950/50 via-transparent to-yellow-200/10" />
      </div>

      {/* Floating shapes */}
      <div className="absolute top-24 right-10 w-40 h-40 rounded-full bg-yellow-300/25 blur-3xl animate-float-slow z-[1]" />
      <div className="absolute bottom-32 left-10 w-56 h-56 rounded-full bg-sky-300/30 blur-3xl animate-float z-[1]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-36 md:pt-44 pb-24 md:pb-32">
        <div className="max-w-3xl">
          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-white/60 px-4 py-1.5 text-[11px] md:text-xs tracking-[0.2em] uppercase text-sky-800 font-semibold shadow-sm"
            data-testid="hero-eyebrow"
          >
            <Compass className="w-3.5 h-3.5" strokeWidth={2.5} />
            {t.hero.eyebrow}
          </motion.div>

          <motion.h1
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            data-testid="hero-title"
            className="font-heading mt-6 text-4xl sm:text-5xl lg:text-7xl tracking-tight text-white leading-[1.02] drop-shadow-[0_4px_24px_rgba(0,30,60,0.45)]"
          >
            {t.hero.title1}{" "}
            <span className="italic font-light text-yellow-300">
              {t.hero.title2}
            </span>
          </motion.h1>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={`tel:${PHONE_RAW}`}
              data-testid="hero-call-cta"
              className="group hidden md:inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-7 py-4 text-base font-semibold shadow-[0_16px_36px_rgba(250,204,21,0.5)] hover:-translate-y-0.5 transition-all"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {t.hero.ctaCall}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={MAPS_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-directions-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-slate-100 text-slate-900 px-7 py-4 text-base font-semibold border border-slate-200 shadow-[0_10px_28px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-all"
            >
              <Navigation className="w-5 h-5 text-sky-600" strokeWidth={2.5} />
              {t.nav.directions}
            </a>
            <a
              href="#fleet"
              data-testid="hero-pricing-cta"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("fleet")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-slate-100 text-slate-900 px-7 py-4 text-base font-semibold border border-slate-200 shadow-[0_10px_28px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-all"
            >
              {t.hero.ctaPricing}
            </a>
          </motion.div>
        </div>
      </div>

      {/* Bottom wave */}
      <svg
        className="absolute bottom-0 left-0 right-0 w-full h-16 md:h-24 text-sky-50 z-10"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="M0,64L60,69.3C120,75,240,85,360,80C480,75,600,53,720,48C840,43,960,53,1080,64C1200,75,1320,85,1380,90.7L1440,96L1440,120L0,120Z"
        />
      </svg>
    </section>
  );
};

export default Hero;
